import { useQuery } from "@tanstack/react-query";
import { Download, FileWarning, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { DocumentFrame } from "@/components/user-data-viewer/document-frame";
import {
  contestSubmissionPreviewQueryOptions,
  useContestSubmissionDownloadUrl,
} from "@/hooks/use-contest-submissions";
import type { ContestManuscript } from "@/types/contest-submission";

import { formatFileSize } from "./format";

interface Props {
  submissionId: string;
  manuscript: ContestManuscript | null;
}

const UNAVAILABLE_MESSAGE: Record<string, string> = {
  too_large: "원고가 커서 미리보기를 만들 수 없습니다. 다운로드해서 확인해 주세요.",
  parse_timeout: "미리보기 변환에 실패했습니다 (시간 초과).",
  parse_error: "미리보기 변환에 실패했습니다.",
  invalid_hwp: "HWP 파일을 읽을 수 없어 미리보기를 만들 수 없습니다.",
  worker_crashed: "미리보기 변환에 실패했습니다.",
  unsupported_format: "미리보기를 지원하지 않는 파일 형식입니다.",
  no_manuscript: "첨부 원고가 없습니다.",
};

function DownloadFallbackButton({ submissionId }: { submissionId: string }) {
  const downloadUrl = useContestSubmissionDownloadUrl();
  const handleDownload = async () => {
    try {
      const url = await downloadUrl.mutateAsync(submissionId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error((e as Error)?.message ?? "다운로드 링크 발급에 실패했습니다.");
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleDownload}
      disabled={downloadUrl.isPending}
    >
      <Download className="mr-1.5 h-3.5 w-3.5" />
      {downloadUrl.isPending ? "발급 중…" : "원고 다운로드"}
    </Button>
  );
}

/**
 * 원고 뷰어 — 다운로드 없이 본문 열람.
 * PDF: presigned URL(inline) iframe — 브라우저 내장 뷰어가 원본 그대로 렌더.
 * hwp/docx: 서버 변환 HTML 을 A4 스타일 DocumentFrame(sandbox iframe)으로 렌더.
 */
export function ManuscriptViewer({ submissionId, manuscript }: Props) {
  const previewQuery = useQuery({
    ...contestSubmissionPreviewQueryOptions(submissionId),
    enabled: Boolean(manuscript),
  });

  if (!manuscript) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center rounded-md border bg-white">
        <p className="text-sm text-muted-foreground">첨부 원고가 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-[80vh] flex-col overflow-hidden rounded-md border bg-white">
      <div className="flex items-center justify-between gap-3 border-b px-4 py-2.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{manuscript.originalFilename}</p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(manuscript.size)} · {manuscript.contentType}
          </p>
        </div>
        <DownloadFallbackButton submissionId={submissionId} />
      </div>

      {previewQuery.isPending && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-sm">
            원고 미리보기를 준비하고 있습니다 — 최초 열람 시 변환에 몇 초 걸릴 수 있습니다.
          </p>
        </div>
      )}

      {previewQuery.isError && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24">
          <FileWarning className="h-6 w-6 text-amber-600" />
          <p className="text-sm text-muted-foreground">
            미리보기를 불러오지 못했습니다: {(previewQuery.error as Error)?.message}
          </p>
          <Button variant="outline" size="sm" onClick={() => previewQuery.refetch()}>
            다시 시도
          </Button>
        </div>
      )}

      {previewQuery.data?.kind === "PDF" && previewQuery.data.url && (
        <iframe
          src={previewQuery.data.url}
          title="원고 PDF"
          className="min-h-[80vh] w-full flex-1 border-0 bg-white"
        />
      )}

      {previewQuery.data?.kind === "HTML" && (
        <DocumentFrame html={previewQuery.data.html ?? ""} />
      )}

      {previewQuery.data?.kind === "UNAVAILABLE" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24">
          <FileWarning className="h-6 w-6 text-amber-600" />
          <p className="text-sm text-muted-foreground">
            {UNAVAILABLE_MESSAGE[previewQuery.data.errorCode ?? ""] ??
              "미리보기를 만들 수 없습니다. 다운로드해서 확인해 주세요."}
          </p>
        </div>
      )}
    </div>
  );
}
